import { type ChangeEvent, type FormEvent } from 'react'
import { snapshot } from 'valtio'
import lodashCloneDeep from 'lodash/cloneDeep'
import { type SingleValue } from 'react-select'
import { addToArray, removeFromArray } from '../../utils/arrayUtils'
import { virtualTerminalProxy } from '../../../routes/app/virtual-terminal/index/virtualTerminalProxy'
import { resetCreditCardAddProxy, creditCardListProxy } from '../../proxies/creditCardListProxy'
import { resetVirtualTerminalCardAddProxy } from '../CardAddSlideOver/cardAddProxy'
import { executeCustomersSearch } from '../../apiUtils/executeCustomersSearch'
import { executeCustomerCreate } from '../../apiUtils/executeCustomerCreate'
import { customerProxy } from '../../proxies/customerProxy'
import {
  VirtualTerminalProxyFormFieldNamesEnum
} from '../../types/virtualTerminalProxyFormFieldNamesEnum'
import {
  populateServiceAddressInfoFromCustomer
} from '../../../routes/app/virtual-terminal/index/Payment/PaymentDetails/utils'
import {
  type GlobalReactSelectOptionStringValueType
} from '../../types/GlobalReactSelectOptionStringValueType'
import { customerListProxy } from '../../proxies/customerListProxy'
import { recurringPaymentNewProxy } from '../../../routes/app/recurring-charges/new/recurringPaymentNewProxy'
import {
  RecurringPaymentNewProxyFormFieldNamesEnum
} from '../../../routes/app/recurring-charges/new/recurringPaymentNewProxyFormFieldNamesEnum'
import {
  populateServiceAddressInfoFromCustomerRecurringConfig
} from '../../../routes/app/recurring-charges/new/CustomerSection/utils'
import { GtagEvents, gtmTrackButtonPress } from '../../utils/gtmUtils'
import { resetCustomerAddProxy, customerAddProxy } from './customerAddProxy'
import { executeCustomerUpdate } from './executeCustomerUpdate'
import { validators } from './validators'
import { normalizers } from './normalizers'
import { type FormFieldNamesEnum } from './formFieldNamesEnum'
import { trimmers } from './trimmers'

export const closeModal = (): void => {
  customerAddProxy.displaySlideOver = false
  customerAddProxy.parentCustomer = null
  gtmTrackButtonPress(GtagEvents.CUSTOMER_MODAL_CANCEL)
}

export const closeCustomerEditModal = (): void => {
  customerProxy.displayEditSlideOver = false
  customerProxy.displayParentSearchSection = false
  customerProxy.selectedParentCustomer = null
  resetCustomerAddProxy()
}

export const handleChange = (fieldName: FormFieldNamesEnum) => (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
  // @ts-expect-error
  const val: string = e.target.type === 'checkbox' ? e.target.checked : e.target.value
  const normalizer = normalizers[fieldName]
  const normalized = normalizer ? normalizer(val) : val
  customerAddProxy.formValues[fieldName] = normalized
  customerAddProxy.formDirty = addToArray(customerAddProxy.formDirty, fieldName)
  const validator = validators[fieldName]
  if (validator) {
    customerAddProxy.formErrors[fieldName] = validator(normalized)
  }
}

export const handleBlur = (fieldName: FormFieldNamesEnum) => (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
  // @ts-expect-error
  const val: string = e.target.type === 'checkbox' ? e.target.checked : e.target.value
  const trimmer = trimmers[fieldName]
  const trimmed = trimmer ? trimmer(val) : val
  customerAddProxy.formValues[fieldName] = trimmed

  const validator = validators[fieldName]
  if (validator) {
    customerAddProxy.formErrors[fieldName] = validator(trimmed)
  }
  customerAddProxy.formBlurred = addToArray(customerAddProxy.formBlurred, fieldName)
}

export const handleSubmitForTerminalCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()

  gtmTrackButtonPress(GtagEvents.CUSTOMER_MODAL_SUBMIT)

  const result = await executeCustomerCreate()
  const { apiCallCustomerCreate } = snapshot(customerAddProxy)
  if (apiCallCustomerCreate.error ?? !result?.id) { return }

  resetCustomerAddProxy()
  resetCreditCardAddProxy()
  creditCardListProxy.customerCards = []
  resetVirtualTerminalCardAddProxy()
  virtualTerminalProxy.customerCard = null
  virtualTerminalProxy.customer = result
  virtualTerminalProxy.form.dirty = addToArray(virtualTerminalProxy.form.dirty, VirtualTerminalProxyFormFieldNamesEnum.Customer)
  virtualTerminalProxy.form.dirty = removeFromArray(virtualTerminalProxy.form.dirty, VirtualTerminalProxyFormFieldNamesEnum.AchAccount)
  virtualTerminalProxy.form.dirty = removeFromArray(virtualTerminalProxy.form.dirty, VirtualTerminalProxyFormFieldNamesEnum.CustomerCard)
  populateServiceAddressInfoFromCustomer()
}

export const handleSubmitForRecurringConfigurationCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()
  gtmTrackButtonPress(GtagEvents.CUSTOMER_MODAL_SUBMIT)

  const result = await executeCustomerCreate()
  const { apiCallCustomerCreate } = snapshot(customerAddProxy)
  if (apiCallCustomerCreate.error ?? !result?.id) { return }

  resetCustomerAddProxy()
  resetCreditCardAddProxy()
  creditCardListProxy.customerCards = []
  resetVirtualTerminalCardAddProxy()
  recurringPaymentNewProxy.customerCard = null
  recurringPaymentNewProxy.customer = result
  recurringPaymentNewProxy.form.dirty = addToArray(recurringPaymentNewProxy.form.dirty, RecurringPaymentNewProxyFormFieldNamesEnum.Customer)
  recurringPaymentNewProxy.form.dirty = removeFromArray(recurringPaymentNewProxy.form.dirty, RecurringPaymentNewProxyFormFieldNamesEnum.CustomerCard)
  populateServiceAddressInfoFromCustomerRecurringConfig()
}

export const handleSubmitForCustomerUpdate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()

  gtmTrackButtonPress(GtagEvents.CUSTOMER_MODAL_SUBMIT)

  const result = await executeCustomerUpdate()
  const { apiCallCustomerUpdate } = snapshot(customerProxy)
  if (apiCallCustomerUpdate.error ?? !result?.id) { return }

  customerAddProxy.displaySlideOver = false
  resetCustomerAddProxy()

  customerProxy.customer = result
}

export async function handleSelectParentCustomer (newValue: SingleValue<GlobalReactSelectOptionStringValueType>): Promise<void> {
  const customerId = newValue?.value ?? null
  const { customers } = snapshot(customerListProxy)
  const { customer } = snapshot(customerProxy)
  const selectedCustomer = customers.find(c => c.id === customerId)

  if (selectedCustomer) {
    // If we have a customer that we are editing, don't allow the parent customer to be the same as the base...BC
    if (customer && customer.id === selectedCustomer.id) {
      return
    }

    customerAddProxy.parentCustomer = lodashCloneDeep(selectedCustomer)
  } else {
    customerAddProxy.parentCustomer = null
  }
}

export const handleSubmitForCustomerListCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()

  gtmTrackButtonPress(GtagEvents.CUSTOMER_MODAL_SUBMIT)

  const result = await executeCustomerCreate()
  const { apiCallCustomerCreate } = snapshot(customerAddProxy)
  if (apiCallCustomerCreate.error ?? !result?.id) { return }

  resetCustomerAddProxy()
  void executeCustomersSearch({ displayLoader: false })
}
